// Reusable sigma + force-atlas canvas.
//
// Pure presentation: takes nodes + edges as props and renders. No
// data fetching, no URL coupling, no selection panel. The /projects/
// :id/graph view is a richer wrapper around this (eventually); the
// /welcome surface drives it directly from turn SSE events.
//
// IDs in the props are opaque keys — they don't have to be the
// engine's internal IDs. The onboarding flow uses external_ids
// (e.g. "user:abc123"); the project graph view uses the engine's
// returned ids. Sigma doesn't care either way.

import Graph from "graphology";
import { useEffect, useMemo, useRef } from "react";
import Sigma from "sigma";
import { EdgeLineProgram } from "sigma/rendering";

import { assignCommunityLayout } from "@/components/canvas/community-layout";
import { communityHue, convexHull, padHull } from "@/components/canvas/hull";
import {
	drawSourceBadge,
	normalizeSourceGlyphKey,
	SOURCE_GLYPH,
	SourceBrandIcon,
	sourceGlyphFor,
} from "@/components/canvas/source-glyph";

export interface CanvasNode {
	id: string;
	label: string;
	type: string;
	// Spec 34. Where this node was ingested from (`github`, `gmail`,
	// `inbound`, etc.). Optional; absent → no glyph drawn.
	source?: string | null;
}

export interface CanvasEdge {
	from: string;
	to: string;
	relation: string;
}

interface GraphCanvasProps {
	nodes: CanvasNode[];
	edges: CanvasEdge[];
	/** Optional accent color per node type. Falls back to neutral. */
	colorForType?: (t: string) => string;
	className?: string;
}

const NODE_FILL_DEFAULT = "#d4d4d8";
const NODE_SIZE_BASE = 5;
const NODE_DEGREE_SCALE = 0.6;
const NODE_DEGREE_CAP = 12;
const EDGE_FILL = "rgba(255,255,255,0.22)";

export function GraphCanvas({
	nodes,
	edges,
	colorForType,
	className,
}: GraphCanvasProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const sigmaRef = useRef<Sigma | null>(null);

	// Memoize the stringified node/edge keys so we don't re-run sigma
	// init on identity changes to the array references (React tends
	// to recreate arrays on every render).
	const nodesKey = useMemo(
		() =>
			nodes
				.map((n) => `${n.id}:${n.label}:${n.type}:${n.source ?? ""}`)
				.join("|"),
		[nodes],
	);

	// Distinct sources present in the current view, for the legend
	// chip strip. Memoized so we don't recompute on every render.
	const presentSources = useMemo(() => {
		const seen = new Set<string>();
		for (const n of nodes) {
			const key = normalizeSourceGlyphKey(n.source);
			if (key && SOURCE_GLYPH[key]) seen.add(key);
		}
		return [...seen];
	}, [nodes]);
	const edgesKey = useMemo(
		() => edges.map((e) => `${e.from}->${e.to}:${e.relation}`).join("|"),
		[edges],
	);

	useEffect(() => {
		const el = containerRef.current;
		if (!el || nodes.length === 0) return;

		const g = new Graph({ multi: true, type: "directed" });
		nodes.forEach((n, i) => {
			const angle = (2 * Math.PI * i) / Math.max(nodes.length, 1);
			g.addNode(n.id, {
				x: Math.cos(angle),
				y: Math.sin(angle),
				size: NODE_SIZE_BASE,
				color: colorForType?.(n.type) ?? NODE_FILL_DEFAULT,
				label: n.label,
				// `_type` not `type`: `type` is reserved by sigma for node
				// program selection (setting it crashes the renderer).
				_type: n.type,
				_baseSize: NODE_SIZE_BASE,
				// Spec 37: synthetic community aggregates anchor the layout
				// but are not drawn.
				hidden: n.type === "community",
			});
		});
		edges.forEach((e) => {
			if (!g.hasNode(e.from) || !g.hasNode(e.to)) return;
			g.addEdge(e.from, e.to, {
				size: 0.3,
				color: EDGE_FILL,
				label: e.relation,
				relation: e.relation,
			});
		});

		g.forEachNode((id) => {
			const deg = g.degree(id);
			const size =
				NODE_SIZE_BASE + Math.min(deg, NODE_DEGREE_CAP) * NODE_DEGREE_SCALE;
			g.setNodeAttribute(id, "size", size);
			g.setNodeAttribute(id, "_baseSize", size);
		});

		// Spec 37: community-aware layout (falls back to plain ForceAtlas2
		// with LinLog when the canvas has no community structure, which is
		// the usual case mid-onboarding).
		assignCommunityLayout(g);

		if (sigmaRef.current) {
			sigmaRef.current.kill();
			sigmaRef.current = null;
		}

		const renderer = new Sigma(g, el, {
			renderLabels: true,
			renderEdgeLabels: true,
			edgeProgramClasses: { line: EdgeLineProgram },
			defaultEdgeType: "line",
			defaultNodeColor: NODE_FILL_DEFAULT,
			defaultEdgeColor: EDGE_FILL,
			labelColor: { color: "rgba(255,255,255,0.85)" },
			labelSize: 12,
			labelWeight: "500",
			labelFont: "Inter, system-ui, sans-serif",
			edgeLabelColor: { color: "rgba(255,255,255,0.4)" },
			edgeLabelSize: 9,
			edgeLabelFont: "Inter, system-ui, sans-serif",
			labelDensity: 1,
			labelGridCellSize: 200,
			labelRenderedSizeThreshold: 6,
			// Disable sigma's default hover-label box (opaque white
			// rectangle behind the label, obscuring the node and
			// other labels nearby). Matches /projects/graph's
			// treatment.
			defaultDrawNodeHover: () => {},
		});
		sigmaRef.current = renderer;

		// Spec 34. Build a source-by-id lookup for the draw pass.
		const sourceById = new Map<string, string>();
		for (const n of nodes) {
			if (n.source) sourceById.set(n.id, n.source);
		}

		// Hull canvas behind sigma's layers (so territories don't fog the
		// nodes), badge canvas on top. See graph.tsx for the same pattern.
		const mkCanvas = () => {
			const c = document.createElement("canvas");
			c.style.position = "absolute";
			c.style.inset = "0";
			c.style.width = "100%";
			c.style.height = "100%";
			c.style.pointerEvents = "none";
			return c;
		};
		const hullCanvas = mkCanvas();
		const overlay = mkCanvas();
		el.insertBefore(hullCanvas, el.firstChild);
		el.appendChild(overlay);

		const fit = (canvas: HTMLCanvasElement) => {
			const ctx = canvas.getContext("2d");
			if (!ctx) return null;
			const dpr = window.devicePixelRatio || 1;
			const cw = el.clientWidth;
			const ch = el.clientHeight;
			if (canvas.width !== cw * dpr || canvas.height !== ch * dpr) {
				canvas.width = cw * dpr;
				canvas.height = ch * dpr;
			}
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			ctx.clearRect(0, 0, cw, ch);
			return ctx;
		};

		const draw = () => {
			const hullCtx = fit(hullCanvas);
			const badgeCtx = fit(overlay);
			if (!hullCtx || !badgeCtx) return;

			// Spec 37: community territories on the behind canvas (no-op
			// when the canvas has no community structure, the usual case).
			const byCommunity = new Map<string, { x: number; y: number }[]>();
			g.forEachNode((id, attrs) => {
				if (attrs.hidden) return;
				const community = attrs._community as string | null | undefined;
				if (!community) return;
				const vp = renderer.graphToViewport({ x: attrs.x, y: attrs.y });
				const pts = byCommunity.get(community);
				if (pts) pts.push(vp);
				else byCommunity.set(community, [vp]);
			});
			for (const [community, pts] of byCommunity) {
				if (pts.length < 3) continue;
				const hull = padHull(convexHull(pts), 16);
				if (hull.length < 3) continue;
				const hue = communityHue(community);
				hullCtx.beginPath();
				hull.forEach((p, i) =>
					i === 0 ? hullCtx.moveTo(p.x, p.y) : hullCtx.lineTo(p.x, p.y),
				);
				hullCtx.closePath();
				hullCtx.fillStyle = `hsla(${hue}, 70%, 60%, 0.12)`;
				hullCtx.fill();
				hullCtx.strokeStyle = `hsla(${hue}, 70%, 65%, 0.32)`;
				hullCtx.lineWidth = 1;
				hullCtx.stroke();
			}

			g.forEachNode((id, attrs) => {
				const source = sourceById.get(id);
				if (!source) return;
				const glyph = sourceGlyphFor(source);
				if (!glyph) return;
				const screenR = renderer.scaleSize(attrs.size ?? NODE_SIZE_BASE);
				if (screenR < 6) return;
				const vp = renderer.graphToViewport({ x: attrs.x, y: attrs.y });
				const badgeR = Math.min(Math.max(screenR * 0.42, 4), 8);
				const offset = screenR * 0.62;
				drawSourceBadge(badgeCtx, vp.x + offset, vp.y + offset, badgeR, glyph);
			});
		};

		renderer.on("afterRender", draw);
		draw();

		return () => {
			renderer.off("afterRender", draw);
			renderer.kill();
			if (hullCanvas.parentNode === el) el.removeChild(hullCanvas);
			if (overlay.parentNode === el) el.removeChild(overlay);
			if (sigmaRef.current === renderer) sigmaRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nodesKey, edgesKey, colorForType]);

	// Container stays as the single, top-level node so sigma's
	// dimension measurement matches the styled element directly
	// (callers pass `absolute inset-0` etc. expecting that). The
	// legend renders as a sibling — it positions against the
	// nearest positioned ancestor (typically the same flex-item
	// that wraps GraphCanvas on /welcome), not against this
	// component's own box.
	return (
		<>
			<div ref={containerRef} className={className} />
			{presentSources.length > 1 && (
				<div
					className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/40 backdrop-blur-sm text-[10px] text-[var(--color-text-muted)] pointer-events-none z-10"
					aria-hidden="true"
				>
					{presentSources.map((s) => {
						const g = SOURCE_GLYPH[s];
						if (!g) return null;
						return (
							<span key={s} className="inline-flex items-center gap-1">
								<SourceBrandIcon source={s} className="w-2.5 h-2.5" />
								{g.label}
							</span>
						);
					})}
				</div>
			)}
		</>
	);
}
